import MapboxDraw from '@mapbox/mapbox-gl-draw';

import { GeoAreaEditSessionService } from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import {
  GeoJsonPolygon,
  Ring,
  holeRings,
  isDrawablePolygon,
  makePolygon,
  removeRingVertex
} from '@core/utils/geo-polygon.utils';

import {
  outerRingVertexIndex,
  placedVertexCount
} from '@features/components/visualization/mapbox/map-elements/map-area-editor.utils';
import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';

const DRAW_FEATURE_ID = 'geo-area-under-edit';

// Draw paints a vertex as a 5 px circle. Hitting that twice in a row is a harder gesture
// than it looks, so the double-click searches a small box around the cursor instead.
const VERTEX_HIT_TOLERANCE_PX = 6;

// GL paint expressions cannot read CSS custom properties, so the palette used for the
// read-only rendering (AREA_COLOR in user-geographic-area-v1.ts) is repeated here as a
// literal instead of a design token, and used to override mapbox-gl-draw's own theme,
// which otherwise paints every zone in its default cyan/orange regardless of app theme.
const DRAW_ZONE_COLOR = '#0f6b73';
const DRAW_VERTEX_HALO_COLOR = '#ffffff';

// Layer ids and filters mirror @mapbox/mapbox-gl-draw's built-in theme (see
// node_modules/@mapbox/mapbox-gl-draw/src/lib/theme.js); only the colours differ.
const DRAW_STYLES: object[] = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': DRAW_ZONE_COLOR,
      'fill-opacity': 0.16
    }
  },
  {
    id: 'gl-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': DRAW_ZONE_COLOR,
      'line-width': 2
    }
  },
  {
    id: 'gl-draw-vertex-outer',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select']
    ],
    paint: {
      'circle-radius': 5,
      'circle-color': DRAW_VERTEX_HALO_COLOR
    }
  },
  {
    id: 'gl-draw-vertex-inner',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select']
    ],
    paint: {
      'circle-radius': 3,
      'circle-color': DRAW_ZONE_COLOR
    }
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 3,
      'circle-color': DRAW_ZONE_COLOR
    }
  }
];

export class MapAreaEditor implements MapElement {
  private draw: MapboxDraw | undefined;
  private isAttached = false;
  /** Draw fires delete/create while the editor rewrites its own feature; those are not edits. */
  private isReseeding = false;
  private wasDoubleClickZoomEnabled = false;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly session: GeoAreaEditSessionService
  ) {}

  init(): void {
    // Draw brings its own sources and layers, added and removed with the control.
  }

  registerEvents(): void {
    // Draw is attached and detached by syncSession, driven from the component effect.
  }

  destroy(): void {
    this.detach();
  }

  /** Called from a component effect whenever the session's active uid or geometry changes. */
  syncSession(): void {
    if (this.session.isActive() && !this.isAttached) {
      this.attach();
    } else if (!this.session.isActive() && this.isAttached) {
      this.detach();
    } else if (this.isAttached) {
      this.reconcile();
    }
  }

  private attach(): void {
    if (this.isAttached) {
      return;
    }
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: 'simple_select',
      styles: DRAW_STYLES
    });
    this.map.addControl(this.draw);
    this.isAttached = true;

    // A double-click removes the vertex under the cursor, so it must not also zoom.
    this.wasDoubleClickZoomEnabled = this.map.doubleClickZoom.isEnabled();
    this.map.doubleClickZoom.disable();

    this.seed(this.session.geometry());

    this.map.on('draw.create', this.onDrawChanged);
    this.map.on('draw.update', this.onDrawChanged);
    this.map.on('draw.delete', this.onDrawDeleted);
    this.map.on('draw.modechange', this.onDrawModeChange);
    this.map.on('draw.render', this.onDrawRender);
    this.map.on('dblclick', this.onDoubleClick);
    this.session.setDrawingFinalizer(this.finishDrawing);
  }

  private detach(): void {
    if (!this.draw) {
      return;
    }
    this.map.off('draw.create', this.onDrawChanged);
    this.map.off('draw.update', this.onDrawChanged);
    this.map.off('draw.delete', this.onDrawDeleted);
    this.map.off('draw.modechange', this.onDrawModeChange);
    this.map.off('draw.render', this.onDrawRender);
    this.map.off('dblclick', this.onDoubleClick);
    this.session.setDrawingFinalizer(null);
    this.session.setPendingVertices(0);
    if (this.wasDoubleClickZoomEnabled) {
      this.map.doubleClickZoom.enable();
    }
    this.map.removeControl(this.draw);
    this.draw = undefined;
    this.isAttached = false;
  }

  /**
   * Puts Draw in front of `geometry`: adjusting it when it is drawable, otherwise placing
   * a fresh ring. Draw's `add` throws on invalid GeoJSON, so a truncated or mis-nested
   * stored geometry starts a new drawing rather than seeding a ring Draw would reject.
   */
  private seed(geometry: GeoJsonPolygon | null): void {
    if (!this.draw) {
      return;
    }

    this.isReseeding = true;
    try {
      this.draw.deleteAll();
      if (geometry && isDrawablePolygon(geometry)) {
        this.draw.add({
          id: DRAW_FEATURE_ID,
          type: 'Feature',
          properties: {},
          geometry
        });
        this.draw.changeMode('direct_select', { featureId: DRAW_FEATURE_ID });
      } else {
        this.draw.changeMode('draw_polygon');
      }
    } finally {
      this.isReseeding = false;
    }
  }

  /**
   * Recovers Draw whenever the session holds no geometry but Draw is not collecting one:
   * deleting the feature, clearing it from the panel, or escaping a fresh draw all leave
   * Draw sitting in simple_select with no way back in. Wired to draw.modechange too, since
   * an aborted in-progress polygon changes mode without firing draw.delete.
   *
   * The mode check is what stops it from firing mid-draw: draw_polygon reports no committed
   * feature until the ring closes, and re-entering the same mode would restart the ring.
   */
  private reconcile(): void {
    if (!this.draw || this.draw.getMode() === 'draw_polygon') {
      return;
    }
    if (this.session.geometry() === null) {
      this.seed(null);
    }
  }

  private readonly onDrawModeChange = (): void => {
    this.reconcile();
    this.reportPendingVertices();
  };

  /**
   * Draw re-renders on every cursor move, so this runs constantly — but the count only
   * changes on a click, and setting a signal to the value it already holds is inert.
   */
  private readonly onDrawRender = (): void => {
    this.reportPendingVertices();
  };

  private reportPendingVertices(): void {
    const collecting = this.draw?.getMode() === 'draw_polygon';
    const ring = collecting ? this.drawnOuterRing() : null;
    this.session.setPendingVertices(ring ? placedVertexCount(ring) : 0);
  }

  /**
   * Closes a ring the user has not closed themselves, so Done finishes a drawing without
   * an enter key first. Leaving draw_polygon is what makes Draw commit the ring and fire
   * draw.create; it discards a ring too short to be a polygon, so the panel only offers
   * Done once enough vertices are down.
   */
  private readonly finishDrawing = (): void => {
    if (!this.draw || this.draw.getMode() !== 'draw_polygon') {
      return;
    }
    this.draw.changeMode('simple_select');
  };

  /** The exterior ring Draw is currently holding, in the order its `coord_path`s index. */
  private drawnOuterRing(): Ring | null {
    const feature = this.draw?.getAll().features[0];
    if (!feature || feature.geometry.type !== 'Polygon') {
      return null;
    }
    return (feature.geometry.coordinates[0] as Ring | undefined) ?? null;
  }

  private readonly onDrawChanged = (event: { features: GeoJSON.Feature[] }): void => {
    if (this.isReseeding) {
      return;
    }

    const feature = event.features[0];
    if (!feature || feature.geometry.type !== 'Polygon') {
      return;
    }

    const drawn = feature.geometry.coordinates as Ring[];
    const existing = this.session.geometry();
    // Draw cannot author interior rings, so the ones already stored are re-attached
    // rather than read back from the editor.
    const holes = existing ? holeRings(existing) : [];
    const next: GeoJsonPolygon = makePolygon(drawn[0], holes);
    this.session.setGeometry(next);
  };

  private readonly onDrawDeleted = (): void => {
    if (this.isReseeding) {
      return;
    }
    this.session.setGeometry(null);
  };

  /**
   * Removes the vertex under the cursor. Draw has no gesture of its own for this — its
   * only route is selecting a vertex and pressing Delete, which is invisible to anyone
   * who has not been told. Hit-testing on the `meta` property rather than on Draw's layer
   * ids keeps this working across its cold/hot layer split.
   */
  private readonly onDoubleClick = (event: mapboxgl.MapMouseEvent): void => {
    // While a ring is still being placed, a double-click closes it: Draw's own gesture.
    if (!this.draw || this.draw.getMode() === 'draw_polygon') {
      return;
    }

    const { x, y } = event.point;
    const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
      [x - VERTEX_HIT_TOLERANCE_PX, y - VERTEX_HIT_TOLERANCE_PX],
      [x + VERTEX_HIT_TOLERANCE_PX, y + VERTEX_HIT_TOLERANCE_PX]
    ];
    const vertex = this.map
      .queryRenderedFeatures(box)
      .find((feature) => feature.properties?.['meta'] === 'vertex');
    if (!vertex) {
      return;
    }

    const index = outerRingVertexIndex(vertex.properties?.['coord_path']);
    const geometry = this.session.geometry();
    const drawnRing = this.drawnOuterRing();
    if (index === null || !geometry || !drawnRing) {
      return;
    }

    // Indexed against Draw's own ring, never the session's: makePolygon rewinds a
    // clockwise ring on the way into the session, so the two orderings diverge after the
    // first drag and a coord_path read against the session would address another vertex.
    const remaining = removeRingVertex(drawnRing, index);
    if (!remaining) {
      this.session.notify('min-vertices');
      return;
    }

    const next = makePolygon(remaining, holeRings(geometry));
    this.seed(next);
    this.session.setGeometry(next);
  };
}
